import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  ChevronLeft, 
  Plus, 
  Minus, 
  ShoppingCart, 
  Heart,
  AlertTriangle,
  Info,
  Pill,
  Package,
  Clock,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { medicineService } from '@/services/api';

// Transform API medicine data to UI format
const transformMedicine = (apiMedicine, lang) => {
  if (!apiMedicine) return null;
  
  return {
    id: apiMedicine.id,
    name: lang === 'ar' ? (apiMedicine.name_ar || apiMedicine.name) : (apiMedicine.name_en || apiMedicine.name),
    genericName: apiMedicine.generic_name || apiMedicine.name,
    category: lang === 'ar' ? (apiMedicine.category?.name_ar || apiMedicine.category_name || 'أدوية') : (apiMedicine.category?.name || apiMedicine.category_name || 'Medicines'),
    price: parseFloat(apiMedicine.price) || 0,
    image: apiMedicine.image || apiMedicine.media?.[0]?.url || 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400&h=400&fit=crop',
    description: lang === 'ar' ? (apiMedicine.description_ar || apiMedicine.description || '') : (apiMedicine.description_en || apiMedicine.description || ''),
    dosage: apiMedicine.dosage || (lang === 'ar' ? 'حسب إرشادات الطبيب' : 'As per doctor instructions'),
    sideEffects: apiMedicine.side_effects || [],
    warnings: apiMedicine.warnings || [],
    prescription: apiMedicine.prescription_required || false,
    stock: apiMedicine.stock || apiMedicine.quantity || 0,
    manufacturer: apiMedicine.manufacturer || apiMedicine.brand || (lang === 'ar' ? 'غير محدد' : 'Unknown'),
    form: apiMedicine.form || (lang === 'ar' ? 'أقراص' : 'Tablets'),
    packSize: apiMedicine.pack_size || (lang === 'ar' ? 'عبوة واحدة' : '1 pack'),
  };
};

// Fallback mock medicine data when API is unavailable
const getMockMedicine = (id, lang) => {
  const medicines = {
    1: {
      id: 1,
      name: lang === 'ar' ? 'بانادول' : 'Panadol',
      genericName: lang === 'ar' ? 'باراسيتامول' : 'Paracetamol',
      category: lang === 'ar' ? 'مسكنات' : 'Pain Relief',
      price: 5.99,
      image: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400&h=400&fit=crop',
      description: lang === 'ar' 
        ? 'مسكن للآلام وخافض للحرارة. يستخدم لتخفيف الصداع، آلام العضلات، التهاب المفاصل، آلام الظهر، آلام الأسنان، نزلات البرد والحمى.'
        : 'Pain reliever and fever reducer. Used to relieve headaches, muscle aches, arthritis, backaches, toothaches, colds, and fevers.',
      dosage: lang === 'ar' 
        ? '500 مجم - 1000 مجم كل 4-6 ساعات حسب الحاجة'
        : '500mg - 1000mg every 4-6 hours as needed',
      sideEffects: lang === 'ar'
        ? ['الغثيان', 'آلام المعدة', 'فقدان الشهية']
        : ['Nausea', 'Stomach pain', 'Loss of appetite'],
      warnings: lang === 'ar'
        ? ['لا تتجاوز 4000 مجم في اليوم', 'تجنب مع الكحول', 'استشر طبيبك إذا كنت تعاني من مشاكل في الكبد']
        : ['Do not exceed 4000mg per day', 'Avoid with alcohol', 'Consult doctor if you have liver problems'],
      prescription: false,
      stock: 50,
      manufacturer: lang === 'ar' ? 'جلاكسو سميث كلاين' : 'GlaxoSmithKline',
      form: lang === 'ar' ? 'أقراص' : 'Tablets',
      packSize: lang === 'ar' ? '24 قرص' : '24 tablets',
    },
    2: {
      id: 2,
      name: lang === 'ar' ? 'أموكسيسيلين' : 'Amoxicillin',
      genericName: lang === 'ar' ? 'أموكسيسيلين' : 'Amoxicillin',
      category: lang === 'ar' ? 'مضادات حيوية' : 'Antibiotics',
      price: 12.99,
      image: 'https://images.unsplash.com/photo-1585435557343-3b092031a831?w=400&h=400&fit=crop',
      description: lang === 'ar'
        ? 'مضاد حيوي واسع الطيف يستخدم لعلاج مجموعة متنوعة من الالتهابات البكتيرية.'
        : 'Broad-spectrum antibiotic used to treat various bacterial infections.',
      dosage: lang === 'ar'
        ? '250 مجم - 500 مجم كل 8 ساعات'
        : '250mg - 500mg every 8 hours',
      sideEffects: lang === 'ar'
        ? ['الإسهال', 'الغثيان', 'الطفح الجلدي']
        : ['Diarrhea', 'Nausea', 'Skin rash'],
      warnings: lang === 'ar'
        ? ['يتطلب وصفة طبية', 'أكمل الدورة كاملة', 'أخبر طبيبك عن أي حساسية للبنسلين']
        : ['Prescription required', 'Complete full course', 'Tell doctor about penicillin allergies'],
      prescription: true,
      stock: 30,
      manufacturer: lang === 'ar' ? 'فايزر' : 'Pfizer',
      form: lang === 'ar' ? 'كبسولات' : 'Capsules',
      packSize: lang === 'ar' ? '21 كبسولة' : '21 capsules',
    },
    3: {
      id: 3,
      name: lang === 'ar' ? 'فيتامين سي' : 'Vitamin C',
      genericName: lang === 'ar' ? 'حمض الأسكوربيك' : 'Ascorbic Acid',
      category: lang === 'ar' ? 'فيتامينات' : 'Vitamins',
      price: 8.99,
      image: 'https://images.unsplash.com/photo-1550572017-edd951aa8f72?w=400&h=400&fit=crop',
      description: lang === 'ar'
        ? 'مكمل غذائي لتقوية المناعة ودعم صحة البشرة والأنسجة.'
        : 'Dietary supplement to boost immunity and support skin and tissue health.',
      dosage: lang === 'ar'
        ? '500 مجم - 1000 مجم يومياً'
        : '500mg - 1000mg daily',
      sideEffects: lang === 'ar'
        ? ['اضطراب المعدة', 'حرقة المعدة']
        : ['Stomach upset', 'Heartburn'],
      warnings: lang === 'ar'
        ? ['لا تتجاوز الجرعة الموصى بها', 'استشر طبيبك إذا كنت حاملاً']
        : ['Do not exceed recommended dose', 'Consult doctor if pregnant'],
      prescription: false,
      stock: 100,
      manufacturer: lang === 'ar' ? 'ناتشرز باونتي' : "Nature's Bounty",
      form: lang === 'ar' ? 'أقراص قابلة للمضغ' : 'Chewable tablets',
      packSize: lang === 'ar' ? '60 قرص' : '60 tablets',
    },
  };

  return medicines[id] || medicines[1];
};

const MedicineDetailPage = () => {
  const { i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const lang = i18n.language || 'ar';
  const isRTL = lang === 'ar';

  const [medicine, setMedicine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isUsingMockData, setIsUsingMockData] = useState(false);

  useEffect(() => {
    const fetchMedicine = async () => {
      setLoading(true);
      setError(null);
      setIsUsingMockData(false);

      try {
        // Try to fetch from API first
        const response = await medicineService.getById(id);
        if (response.data?.data) {
          const transformed = transformMedicine(response.data.data, lang);
          setMedicine(transformed);
        } else {
          throw new Error('No data returned');
        }
      } catch (err) {
        console.log('API not available for medicines, using mock data:', err.message);
        // Fall back to mock data
        setMedicine(getMockMedicine(id, lang));
        setIsUsingMockData(true);
      } finally {
        setLoading(false);
      }
    };

    fetchMedicine();
  }, [id, lang]);

  const handleAddToCart = () => {
    if (medicine.prescription) {
      toast.error(
        lang === 'ar' ? 'هذا الدواء يتطلب وصفة طبية' : 'This medicine requires a prescription'
      );
      return;
    }
    
    toast.success(
      lang === 'ar' ? 'تمت الإضافة إلى السلة' : 'Added to cart',
      { description: `${medicine.name} x${quantity}` }
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="container mx-auto px-4 py-6">
          <Skeleton className="h-10 w-24 mb-6" />
          <div className="grid lg:grid-cols-2 gap-8">
            <Skeleton className="h-96 w-full rounded-xl" />
            <div className="space-y-4">
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-6 w-1/2" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !medicine) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center" dir={isRTL ? 'rtl' : 'ltr'}>
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">{lang === 'ar' ? 'خطأ' : 'Error'}</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-4">{error}</p>
            <Button onClick={() => navigate(-1)}>
              <ChevronLeft className="h-4 w-4 mr-2" />
              {lang === 'ar' ? 'رجوع' : 'Go Back'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" dir={isRTL ? 'rtl' : 'ltr'} data-testid="medicine-detail-page">
      <div className="container mx-auto px-4 py-6">
        {/* Mock Data Notice */}
        {isUsingMockData && (
          <Card className="mb-4 border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800">
            <CardContent className="p-3 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <p className="text-sm text-amber-700 dark:text-amber-400">
                {lang === 'ar' 
                  ? 'عرض بيانات تجريبية - سيتم تحديث البيانات عند توفر واجهة برمجة التطبيقات'
                  : 'Showing demo data - Will update when API becomes available'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Back Button */}
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
          <ChevronLeft className={`h-4 w-4 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
          {lang === 'ar' ? 'رجوع' : 'Back'}
        </Button>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Image Section */}
          <div className="relative">
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm overflow-hidden">
              <img 
                src={medicine.image} 
                alt={medicine.name} 
                className="w-full h-96 object-cover"
              />
              {medicine.prescription && (
                <Badge className="absolute top-4 left-4 bg-red-500">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  {lang === 'ar' ? 'يتطلب وصفة طبية' : 'Prescription Required'}
                </Badge>
              )}
            </div>
            <Button 
              variant="outline" 
              size="icon" 
              className="absolute top-4 right-4 bg-white dark:bg-slate-800"
              onClick={() => setIsFavorite(!isFavorite)}
              data-testid="medicine-favorite-btn"
            >
              <Heart className={`h-5 w-5 ${isFavorite ? 'fill-red-500 text-red-500' : ''}`} />
            </Button>
          </div>

          {/* Details Section */}
          <div className="space-y-6">
            <div>
              <Badge className="mb-2 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">{medicine.category}</Badge>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white" data-testid="medicine-name">{medicine.name}</h1>
              <p className="text-slate-500 dark:text-slate-400">{medicine.genericName}</p>
            </div>

            <div className="flex items-baseline gap-4">
              <span className="text-3xl font-bold text-green-600" data-testid="medicine-price">${medicine.price}</span>
              <span className="text-sm text-slate-500 dark:text-slate-400">{medicine.packSize}</span>
            </div>

            <p className="text-slate-600 dark:text-slate-300 leading-relaxed">{medicine.description}</p>

            {/* Product Info */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="dark:bg-slate-800 border-0 shadow-sm">
                <CardContent className="p-4 flex items-center gap-3">
                  <Package className="h-5 w-5 text-teal-600" />
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{lang === 'ar' ? 'الشكل' : 'Form'}</p>
                    <p className="font-medium text-slate-900 dark:text-white">{medicine.form}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="dark:bg-slate-800 border-0 shadow-sm">
                <CardContent className="p-4 flex items-center gap-3">
                  <Pill className="h-5 w-5 text-teal-600" />
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{lang === 'ar' ? 'الشركة المصنعة' : 'Manufacturer'}</p>
                    <p className="font-medium text-sm text-slate-900 dark:text-white">{medicine.manufacturer}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Stock Status */}
            <div className="flex items-center gap-2">
              {medicine.stock > 0 ? (
                <>
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="text-green-600 font-medium">
                    {lang === 'ar' ? 'متوفر في المخزون' : 'In Stock'}
                  </span>
                  <span className="text-slate-400">({medicine.stock} {lang === 'ar' ? 'قطعة' : 'units'})</span>
                </>
              ) : (
                <>
                  <Clock className="h-5 w-5 text-red-600" />
                  <span className="text-red-600 font-medium">
                    {lang === 'ar' ? 'غير متوفر' : 'Out of Stock'}
                  </span>
                </>
              )}
            </div>

            {/* Quantity Selector and Add to Cart */}
            {!medicine.prescription && (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    data-testid="medicine-decrease-qty"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="w-12 text-center font-semibold text-lg text-slate-900 dark:text-white" data-testid="medicine-qty">{quantity}</span>
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={() => setQuantity(quantity + 1)}
                    data-testid="medicine-increase-qty"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <Button 
                  className="flex-1 bg-green-600 hover:bg-green-700" 
                  size="lg"
                  onClick={handleAddToCart}
                  disabled={medicine.stock === 0}
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingCart className="h-5 w-5 mr-2" />
                  {lang === 'ar' ? 'أضف إلى السلة' : 'Add to Cart'}
                  <span className="ml-2">${(medicine.price * quantity).toFixed(2)}</span>
                </Button>
              </div>
            )}

            {medicine.prescription && (
              <Card className="border-0 shadow-sm bg-amber-50 dark:bg-amber-900/20">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800 dark:text-amber-400">
                        {lang === 'ar' ? 'يتطلب وصفة طبية' : 'Prescription Required'}
                      </p>
                      <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                        {lang === 'ar' 
                          ? 'هذا الدواء يتطلب وصفة طبية صالحة من طبيب مرخص. يرجى تحميل الوصفة أو استشارة الطبيب.'
                          : 'This medicine requires a valid prescription from a licensed doctor. Please upload a prescription or consult a doctor.'
                        }
                      </p>
                      <Button variant="outline" className="mt-3" size="sm" onClick={() => navigate('/doctors')}>
                        {lang === 'ar' ? 'احجز استشارة طبية' : 'Book a Consultation'}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Additional Information */}
        <div className="mt-8 grid md:grid-cols-3 gap-6">
          {/* Dosage */}
          <Card className="dark:bg-slate-800 border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Info className="h-5 w-5 text-teal-600" />
                {lang === 'ar' ? 'الجرعة' : 'Dosage'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 dark:text-slate-400">{medicine.dosage}</p>
            </CardContent>
          </Card>

          {/* Side Effects */}
          <Card className="dark:bg-slate-800 border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                {lang === 'ar' ? 'الآثار الجانبية' : 'Side Effects'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-1">
                {medicine.sideEffects.length > 0 ? medicine.sideEffects.map((effect, idx) => (
                  <li key={idx} className="text-slate-600 dark:text-slate-400 text-sm flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                    {effect}
                  </li>
                )) : (
                  <li className="text-slate-400 text-sm">{lang === 'ar' ? 'غير محدد' : 'Not specified'}</li>
                )}
              </ul>
            </CardContent>
          </Card>

          {/* Warnings */}
          <Card className="dark:bg-slate-800 border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                {lang === 'ar' ? 'تحذيرات' : 'Warnings'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-1">
                {medicine.warnings.length > 0 ? medicine.warnings.map((warning, idx) => (
                  <li key={idx} className="text-slate-600 dark:text-slate-400 text-sm flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                    {warning}
                  </li>
                )) : (
                  <li className="text-slate-400 text-sm">{lang === 'ar' ? 'غير محدد' : 'Not specified'}</li>
                )}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MedicineDetailPage;
